import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AttendanceClassDateSelector from './AttendanceClassDateSelector';
import AttendanceStudentList from './AttendanceStudentList';
import AttendanceActions from './AttendanceActions';
import AddStudentModal from './AddStudentModal';
import { AttendanceAPI } from '../../api/attendanceAPI';
import { classroomsAPI } from '../../api';
import studentsAPI from '../../api/studentsAPI';
import { periodsAPI } from '../../api/periodsAPI';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../contexts/TenantContext';
import { getTodayLocal, getCurrentTimezoneConfig } from '../../utils/timezoneUtils';
import { convertDateFromUserToSchoolTimezone } from '../../utils/dateTimezoneUtils';
import { isAutoFillEnabled, isAutoFillFromDatabaseEnabled, isAutoFillFromDraftsEnabled } from '../../utils/autoFillUtils';


// Add keyframes for shake animation at the top of the file, after imports
const shakeKeyframes = `
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
}
`;

export default function TakeAttendancePage() {
  const { classId: paramClassId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentSchool } = useTenant();


  // 1️⃣ Available classes from the current school
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(() => {
    // Check URL query params first (for direct navigation from ClassDetail)
    const classFromQuery = searchParams.get('class');
    if (classFromQuery) return classFromQuery;
    
    // Then check URL params, then school-scoped localStorage, then default to empty
    if (paramClassId) return paramClassId;
    
    // Only use localStorage if we have a current school to scope it to
    if (currentSchool) {
      const schoolId = currentSchool.unique_id || currentSchool.unique_id;
      const savedClass = localStorage.getItem(`attendanceReports_lastSelectedClass_${schoolId}`);
      return savedClass || '';
    }
    
    return '';
  });

 
  const { getAccessTokenSilently, loginWithPopup, user } = useAuth0();
  const [periods, setPeriods] = useState([]);

  const [activePeriodId, setActivePeriodId] = useState(() => {
    // Check URL query params for period (for direct navigation from ClassDetail)
    const periodFromQuery = searchParams.get('period');
    return periodFromQuery || '';
  });
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]); // Store all students for the class
  const [historicalStudents, setHistoricalStudents] = useState([]); // Store historical students for current date only
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Caching system for performance optimization
  const [classDataCache, setClassDataCache] = useState(new Map());
  
  //const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(getTodayLocal()); // Use configured timezone for default date
  const [attendance, setAttendance] = useState({});
  const [notes, setNotes] = useState({}); // State for student notes
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState({ message: '', isVisible: false, zIndex: 50 }); // Combined toast state with z-index
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [hasAutoFilledData, setHasAutoFilledData] = useState(false);
  const [autoFillMessage, setAutoFillMessage] = useState('');
  const [autoFillCheckComplete, setAutoFillCheckComplete] = useState(false);
  const [hasDraftData, setHasDraftData] = useState(false);
  const [draftMessage, setDraftMessage] = useState('');
  const [reportStatus, setReportStatus] = useState('no-report'); // 'no-report', 'in-progress', 'saved'

  // Helper function to format timestamps with timezone awareness
  const formatTimestampWithTimezone = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const currentConfig = getCurrentTimezoneConfig();
    
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    // Apply timezone only if not LOCAL (which should use device timezone)
    if (currentConfig && currentConfig !== 'LOCAL') {
      options.timeZone = currentConfig;
    }
    
    return date.toLocaleString('en-US', options);
  };
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  
  // Enable/disable state for students without status
  const [enableUnselectedStudents, setEnableUnselectedStudents] = useState(false);
  const [attendanceBackup, setAttendanceBackup] = useState({}); // Backup attendance when adding students
  const submitBtnRef = useRef(null);
  const isUpdatingFromServerRef = useRef(false); // Track when we're updating from server/draft data
  const loadingTimeoutRef = useRef(null);

  // Add Student Modal state
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  
  // Delete Report Modal state
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  
  // Track if there were students without status when the report was initially loaded
  const [hadStudentsWithoutStatusOnLoad, setHadStudentsWithoutStatusOnLoad] = useState(false);

  // Track if the report was just submitted (vs loaded from database)
  const [reportJustSubmitted, setReportJustSubmitted] = useState(false);

  // Track original student categorization to prevent movement between sections
  const [originalStudentCategories, setOriginalStudentCategories] = useState({});

  // Track which students were loaded from database to distinguish from currently enrolled
  const [studentsFromDatabase, setStudentsFromDatabase] = useState(new Set());

  // Lightweight token cache to prevent multiple /authorize + /token calls
  const tokenCacheRef = useRef({ token: null, exp: 0, inFlight: null });

  const decodeJwtExp = (jwt) => {
    try {
      const payload = jwt.split('.')[1];
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return typeof json.exp === 'number' ? json.exp * 1000 : 0;
    } catch {
      return 0;
    }
  };

  // Memoized API token function (cached + deduped + refresh 60s before expiry)
  const getApiToken = useCallback(async () => {
    const now = Date.now();
    const { token, exp, inFlight } = tokenCacheRef.current;

    // Use cached token if valid for another 60s
    if (token && now < (exp - 60_000)) {
      return token;
    }

    // Reuse in-flight request for concurrent callers
    if (inFlight) {
      return inFlight;
    }

    const request = (async () => {
      try {
        const t = await getAccessTokenSilently({
          authorizationParams: {
            audience: "https://api.arcattend.com",
            scope: 'openid profile email',
          },
          // Use default caching behavior of the SDK
        });
        tokenCacheRef.current.token = t;
        tokenCacheRef.current.exp = decodeJwtExp(t) || (now + 5 * 60_000);
        tokenCacheRef.current.inFlight = null;
        return t;
      } catch (err) {
        const needsConsentOrLogin =
          err?.error === 'consent_required' ||
          err?.error === 'login_required' ||
          /consent required/i.test(err?.message || '');

        if (!needsConsentOrLogin) {
          tokenCacheRef.current.inFlight = null;
          throw err;
        }

        await loginWithPopup({
          authorizationParams: {
            audience: "https://api.arcattend.com",
            scope: 'openid profile email',
          },
        });

        const t2 = await getAccessTokenSilently({
          authorizationParams: {
            audience: "https://api.arcattend.com",
            scope: 'openid profile email',
          },
        });
        tokenCacheRef.current.token = t2;
        tokenCacheRef.current.exp = decodeJwtExp(t2) || (Date.now() + 5 * 60_000);
        tokenCacheRef.current.inFlight = null;
        return t2;
      }
    })();

    tokenCacheRef.current.inFlight = request;
    return request;
  }, [getAccessTokenSilently, loginWithPopup]);


//   const classObj = sampleClasses.find(c => c.id === selectedClass);
//   //const students = selectedClass ? sampleStudentsByClass[selectedClass] : [];
  //const teachers = classObj ? classObj.teachers : [];

   // Fetch class info and students when classId changes
  // Load class info and students
  useEffect(() => {
    if (!currentSchool) return;
    (async () => {
      try {
        const token = await getApiToken();
        // Use getAllClassrooms with teacher filtering (adminView=false) for attendance page
        // This will automatically filter to only show classes the teacher is assigned to
        const classes = await classroomsAPI.getAllClassrooms(token, null, currentSchool.id, false);
        setAvailableClasses(classes);
        
        console.log('🎯 TakeAttendancePage: Loaded classes for attendance (teacher-filtered):', {
          school: currentSchool.id,
          classCount: classes.length,
          classNames: classes.map(c => c.name)
        });
        
        // Validate that currently selected class belongs to this school
        if (selectedClassId) {
          const isValidClass = classes.some(c => c.id.toString() === selectedClassId);
          if (!isValidClass) {
            console.log('🚫 Selected class does not belong to current school, clearing selection');
            setSelectedClassId('');
          }
        }
        
        // Handle URL param class selection
        if (paramClassId && classes.some(c => c.id.toString() === paramClassId)) {
          handleClassChange(paramClassId);
        }
      } catch (err) {
        console.error('Failed to load classes:', err);
      }
    })();
  }, [currentSchool, paramClassId, selectedClassId]); // eslint-disable-line

  // Save selected class to localStorage whenever it changes (scoped to current school)
  useEffect(() => {
    // Only save to localStorage if we have a current school to scope it to
    if (currentSchool) {
      const schoolId = currentSchool.unique_id || currentSchool.unique_id;
      
      if (selectedClassId) {
        localStorage.setItem(`attendanceReports_lastSelectedClass_${schoolId}`, selectedClassId);
        console.log('💾 Saved selected class to school-scoped localStorage:', selectedClassId, 'for school:', schoolId);
      } else {
        // Remove from localStorage when class is cleared
        localStorage.removeItem(`attendanceReports_lastSelectedClass_${schoolId}`);
        console.log('🗑️ Removed saved class from school-scoped localStorage for school:', schoolId);
      }
    }
  }, [selectedClassId, currentSchool]);
  
  // Handle school switching - reset class selection and load school-scoped localStorage
  useEffect(() => {
    if (!currentSchool) {
      // Clear class selection when no school is selected
      setSelectedClassId('');
      return;
    }

    const schoolId = currentSchool.unique_id || currentSchool.unique_id;
    
    // Clean up old non-scoped localStorage key (one-time migration)
    const oldSavedClass = localStorage.getItem('attendanceReports_lastSelectedClass');
    if (oldSavedClass) {
      console.log('🔄 Migrating old localStorage key to school-scoped format');
      localStorage.removeItem('attendanceReports_lastSelectedClass');
      // Don't automatically migrate the value - let user reselect to avoid cross-school data leaks
    }
    
    // If we have paramClassId from URL, use it (validates against available classes later)
    if (paramClassId) {
      setSelectedClassId(paramClassId);
      return;
    }
    
    // If no URL param and no current selection, try to load from school-scoped localStorage
    if (!selectedClassId) {
      const savedClass = localStorage.getItem(`attendanceReports_lastSelectedClass_${schoolId}`);
      if (savedClass) {
        console.log('🔄 Loading saved class for school:', schoolId, 'class:', savedClass);
        setSelectedClassId(savedClass);
      }
    }
    
  }, [currentSchool, paramClassId, selectedClassId]);
  
  // Update selected date when timezone configuration changes
  useEffect(() => {
    const handleTimezoneChange = () => {
      const newTodayLocal = getTodayLocal();
      console.log('🌍 Timezone configuration changed, updating selected date from', selectedDate, 'to', newTodayLocal);
      
      // Only update if current date is "today" (to preserve user-selected dates)
      const physicalToday = new Date().toISOString().split('T')[0];
      if (selectedDate === physicalToday || !selectedDate) {
        setSelectedDate(newTodayLocal);
      }
    };

    // Listen for timezone config changes in localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'arcattend_timezone_config') {
        handleTimezoneChange();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Clean up listener
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [selectedDate]);
  
// useEffect(() => {
//   const cls = availableClasses.find(c => c.id.toString() === selectedClassId);

//   // Prefer a teacher linked to the class (adjust key if your class stores it differently)
//   const teacherId =
//     cls?.synced_teacher?.id ??
//     cls?.synced_teacher?.member_id ??
//     cls?.teacher_id;

//   if (teacherId && String(teacherId).trim() !== '') {
//     setMarkedById(String(teacherId));
//     return;
//   }

//   // Otherwise use the logged-in member returned from /members/me
//   if (meMember?.id && String(meMember.id).trim() !== '') {
//     setMarkedById(String(meMember.id));
//     return;
//   }

//   // (Optional) Otherwise leave blank or pick some other fallback you already have
//   setMarkedById('');
// }, [availableClasses, selectedClassId, meMember]);

  // Cache management functions
  const clearClassDataCache = useCallback((classId = null) => {
    if (classId) {
      const cacheKey = `class_${classId}`;
      setClassDataCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(cacheKey);
        console.log('🗑️ Cleared cache for class', classId);
        return newCache;
      });
    } else {
      setClassDataCache(new Map());
      console.log('🗑️ Cleared entire class data cache');
    }
  }, []);

  // Clear cache when date changes (since attendance data is date-dependent)
  const handleDateChange = useCallback((newDate) => {
    console.log('📅 Date changing from', selectedDate, 'to', newDate);
    clearClassDataCache(); // Clear all cached data when date changes
    setSelectedDate(newDate);
    setAttendance({}); // Clear attendance when date changes
    setNotes({}); // Clear notes when date changes
    setReportStatus('checking'); // Reset status for new date
    setReportJustSubmitted(false); // Reset submitted flag for new date
    setOriginalStudentCategories({}); // Reset student categories for new date
    setStudentsFromDatabase(new Set()); // Reset database tracking for new date
    console.log('📅 Date change complete - attendance, notes, categories and database tracking reset');
  }, [clearClassDataCache, selectedDate]);

  // Clear cache for specific class when class changes
  const handleClassChange = useCallback((newClassId) => {
    if (selectedClassId !== newClassId) {
      clearClassDataCache(selectedClassId); // Clear cache for previous class
      setSelectedClassId(newClassId);
      setAttendance({}); // Clear attendance when class changes
      setNotes({}); // Clear notes when class changes
      setReportStatus('checking'); // Reset status for new class
      setReportJustSubmitted(false); // Reset submitted flag for new class
      setOriginalStudentCategories({}); // Reset student categories for new class
      setStudentsFromDatabase(new Set()); // Reset database tracking for new class
    }
  }, [selectedClassId, clearClassDataCache]);


  // 2) Load periods and students in parallel when class changes
  useEffect(() => {
    if (!selectedClassId) return;
    
    const loadClassData = async () => {
      console.time('⏱️ Load class data');
      setLoadingStudents(true);
      
      // Check cache first
      const cacheKey = `class_${selectedClassId}`;
      const cachedData = classDataCache.get(cacheKey);
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
      
      if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_DURATION) {
        console.log('💾 Loading class data from cache for class', selectedClassId, '(cached', Math.round((Date.now() - cachedData.timestamp) / 1000), 'seconds ago)');
        setPeriods(cachedData.periods);
        setAllStudents(cachedData.students);
        
        if (cachedData.periods.length) {
          // Check if there's a specific period from query params
          const periodFromQuery = searchParams.get('period');
          if (periodFromQuery && cachedData.periods.find(p => p.id === Number(periodFromQuery))) {
            setActivePeriodId(Number(periodFromQuery));
          } else {
            setActivePeriodId(cachedData.periods[0].id);
          }
        } else {
          setActivePeriodId('');
        }
        
        setLoadingStudents(false);
        console.timeEnd('⏱️ Load class data');
        console.log('📊 Loaded from cache:', cachedData.students.length, 'students and', cachedData.periods.length, 'periods for class', selectedClassId);
        return;
      } else if (cachedData) {
        console.log('⏰ Cache expired for class', selectedClassId, '- reloading fresh data');
      }
      
      try {
        const token = await getApiToken();
        
        console.time('⏱️ API calls (parallel)');
        // Load periods and students in parallel for better performance
        const [perList, roster] = await Promise.all([
          periodsAPI.getPeriodsByClassroom(Number(selectedClassId), token),
          studentsAPI.getStudentsByClassroom(Number(selectedClassId), token)
        ]);
        console.timeEnd('⏱️ API calls (parallel)');
        
        console.time('⏱️ Format students');
        // Set periods
        setPeriods(perList);
        if (perList.length) {
          // Check if there's a specific period from query params
          const periodFromQuery = searchParams.get('period');
          if (periodFromQuery && perList.find(p => p.id === Number(periodFromQuery))) {
            setActivePeriodId(Number(periodFromQuery));
          } else {
            setActivePeriodId(perList[0].id);
          }
        } else {
          setActivePeriodId('');
        }
        
        // Format students with optimized avatar generation
        const formattedRoster = Array.isArray(roster)
          ? roster.map(st => {
              const fullName = `${st.first_name || ''} ${st.last_name || ''}`.trim();
              return {
                ...st,
                name: fullName,
                avatar: st.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}`,
              };
            })
          : [];
        console.timeEnd('⏱️ Format students');
        
        setAllStudents(formattedRoster);
        
        console.log(`📊 Loaded ${formattedRoster.length} students and ${perList.length} periods for class ${selectedClassId}`);
        
        // Cache the successfully loaded data
        const cacheData = {
          students: formattedRoster,
          periods: perList,
          timestamp: Date.now()
        };
        
        setClassDataCache(prev => {
          const newCache = new Map(prev);
          
          // Limit cache size to prevent memory issues (keep last 10 classes)
          if (newCache.size >= 10) {
            const oldestKey = newCache.keys().next().value;
            newCache.delete(oldestKey);
            console.log('🗑️ Removed oldest cache entry:', oldestKey);
          }
          
          newCache.set(cacheKey, cacheData);
          console.log('💾 Cached class data for class', selectedClassId, '- cache size:', newCache.size);
          return newCache;
        });
        
      } catch (err) {
        console.error('Failed to load class data:', err);
        setPeriods([]);
        setActivePeriodId('');
        setAllStudents([]);
        setHistoricalStudents([]);
      } finally {
        setLoadingStudents(false);
        console.timeEnd('⏱️ Load class data');
      }
    };

    // Reset all related state when class changes
    setStudents([]);
    setAttendance({});
    setNotes({}); // Clear notes when class changes
    setHistoricalStudents([]); // Clear historical students when class changes
    setCurrentPage(1);
    setHasAutoFilledData(false);
    setAutoFillMessage('');
    
    // Check if any auto-fill features are enabled to determine if we need the loading state
    const databaseAutoFillEnabled = isAutoFillFromDatabaseEnabled() && isAutoFillEnabled();
    const draftAutoFillEnabled = isAutoFillFromDraftsEnabled() && isAutoFillEnabled();
    
    // Set initial report status based on whether we'll be checking for reports
    if (databaseAutoFillEnabled || draftAutoFillEnabled) {
      setReportStatus('checking'); // Show checking immediately if we'll be doing auto-fill
      setAutoFillCheckComplete(false);
    } else {
      console.log('🚀 Auto-fill disabled - component will render immediately without database checks');
      setReportStatus('no-report'); // Only show no-report if auto-fill is disabled
      setAutoFillCheckComplete(true); // Mark as complete immediately for faster rendering
    }
    
    loadClassData();
  }, [selectedClassId]); // eslint-disable-line

  // 3) Filter students by active period (optimized client-side filtering)
  useEffect(() => {
    // Don't clear students if allStudents is empty but we're in the middle of loading/autofill
    if (!allStudents.length) {
      // Only clear students if we're not in the middle of an autofill check
      if (autoFillCheckComplete || !loadingStudents) {
        setStudents([]);
      }
      return;
    }

    console.log('🔍 Period filtering debug:', {
      activePeriodId,
      activePeriodIdType: typeof activePeriodId,
      allStudentsCount: allStudents.length,
      sampleStudentPeriodIds: allStudents.slice(0, 3).map(s => ({ 
        id: s.id, 
        name: s.name, 
        period_id: s.period_id, 
        period_id_type: typeof s.period_id 
      }))
    });

    const filteredStudents = activePeriodId 
      ? allStudents.filter(s => s.period_id === activePeriodId)
      : allStudents;
    
    console.log('📊 Period filter result:', {
      beforeFilter: allStudents.length,
      afterFilter: filteredStudents.length,
      periodMatch: allStudents.filter(s => s.period_id === activePeriodId).length
    });
    
    setStudents(filteredStudents);
    
    setCurrentPage(1);
  }, [allStudents, activePeriodId, autoFillCheckComplete, loadingStudents]);

  // Restore attendance after students are added (runs after period filtering)
  useEffect(() => {
    if (Object.keys(attendanceBackup).length > 0) {
      console.log('🔄 Restoring attendance from backup:', attendanceBackup);
      setAttendance(attendanceBackup);
      setAttendanceBackup({}); // Clear backup after restoring
    }
  }, [attendanceBackup]);

  // 4) Clear attendance when date changes
  useEffect(() => {
    setAttendance({});
    setHasAutoFilledData(false);
    setAutoFillMessage('');
    setHasDraftData(false);
    setDraftMessage('');
    
    // Check if any auto-fill features are enabled to determine if we need the loading state
    const databaseAutoFillEnabled = isAutoFillFromDatabaseEnabled() && isAutoFillEnabled();
    const draftAutoFillEnabled = isAutoFillFromDraftsEnabled() && isAutoFillEnabled();
    
    // Set initial report status based on whether we'll be checking for reports
    if (databaseAutoFillEnabled || draftAutoFillEnabled) {
      setReportStatus('checking'); // Show checking immediately if we'll be doing auto-fill
      setAutoFillCheckComplete(false);
    } else {
      setReportStatus('no-report'); // Only show no-report if auto-fill is disabled
      setAutoFillCheckComplete(true);
    }
  }, [selectedDate]);

  // 4.5) Clear attendance when period changes  
  useEffect(() => {
    setAttendance({});
    setNotes({}); // Clear notes when period changes
    setHasAutoFilledData(false);
    setAutoFillMessage('');
    setHasDraftData(false);
    setDraftMessage('');
    setReportStatus('no-report'); // Reset report status when period changes
    setReportJustSubmitted(false); // Reset submitted flag when period changes
    setOriginalStudentCategories({}); // Reset student categories for new period
    setStudentsFromDatabase(new Set()); // Reset database tracking for new period
    
    // Check if any auto-fill features are enabled to determine if we need the loading state
    const databaseAutoFillEnabled = isAutoFillFromDatabaseEnabled() && isAutoFillEnabled();
    const draftAutoFillEnabled = isAutoFillFromDraftsEnabled() && isAutoFillEnabled();
    
    // Only reset autoFillCheckComplete if we actually need to perform checks
    if (databaseAutoFillEnabled || draftAutoFillEnabled) {
      setReportStatus('checking'); // Show checking status while auto-fill runs
      setAutoFillCheckComplete(false);
    } else {
      // If no auto-fill features are enabled, mark as complete immediately for faster rendering
      console.log('🚀 Auto-fill disabled for period change - skipping checks for better performance');
      setReportStatus('no-report');
      setAutoFillCheckComplete(true);
    }
  }, [activePeriodId]);

  // 4.6) Log auto-fill configuration changes for debugging
  useEffect(() => {
    const config = {
      database: isAutoFillFromDatabaseEnabled(),
      draft: isAutoFillFromDraftsEnabled()
    };
    console.log('🔧 Auto-fill configuration updated:', config);
  }, []); // Only log on mount to avoid spam

  // Manage loading indicator with delay to prevent brief flashes
  useEffect(() => {
    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (!autoFillCheckComplete) {
      // Only show loading indicator after 150ms delay to prevent brief flashes
      loadingTimeoutRef.current = setTimeout(() => {
        setShowLoadingIndicator(true);
      }, 150);
    } else {
      // Immediately hide loading indicator when check is complete
      setShowLoadingIndicator(false);
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [autoFillCheckComplete]);

  // 5) Check for existing attendance (database first, then drafts) before rendering
  useEffect(() => {
    // Only run auto-fill check when it's needed and students are loaded
    if (autoFillCheckComplete || loadingStudents) {
      return;
    }

    // Wait for allStudents to be loaded before proceeding (don't use filtered students)
    // But if loadingStudents is false, we know the loading is complete (even if 0 students)
    if (allStudents.length === 0 && loadingStudents) {
      console.log('⏳ All students still loading, waiting...');
      return;
    }

    const loadExistingAttendance = async () => {
      // Clear historical students at the start of each attendance load (ensures date-specific behavior)
      setHistoricalStudents([]);
      
      // Track historical students found during this load for draft-disabled logic
      let historicalStudentsFound = [];
      
      console.log('🏫 Current school check:', { 
        currentSchool, 
        schoolId: currentSchool?.id, 
        unique_id: currentSchool?.unique_id,
        hasSchool: !!currentSchool 
      });
      
      if (!selectedClassId || !activePeriodId || !selectedDate) {
        setHasAutoFilledData(false);
        setAutoFillMessage('');
        setHasDraftData(false);
        setDraftMessage('');
        setReportStatus('no-report');
        setAutoFillCheckComplete(true); // Mark check as complete
        return;
      }

      if (!currentSchool) {
        console.log('⚠️ No current school available - skipping attendance check');
        setHasAutoFilledData(false);
        setAutoFillMessage('');
        setHasDraftData(false);
        setDraftMessage('');
        setReportStatus('no-report');
        setAutoFillCheckComplete(true);
        return;
      }

      // Calculate filtered students for this period directly (don't rely on students state due to race condition)
      const studentsForThisPeriod = activePeriodId 
        ? allStudents.filter(s => s.period_id === activePeriodId)
        : allStudents;

      console.log('🔄 Auto-fill check starting for:', { 
        selectedClassId, 
        activePeriodId, 
        selectedDate, 
        allStudentsCount: allStudents.length, 
        studentsForThisPeriodCount: studentsForThisPeriod.length,
        currentStudentsStateCount: students.length // This might be stale due to race condition
      });

      // Track loaded attendance data throughout the function
      let loadedAttendanceData = {};

      // Debug: Check the actual students arrays
      console.log('🔍 Students array debug:', {
        allStudents: allStudents.map(s => ({ id: s.id, name: s.name, period_id: s.period_id })),
        filteredStudents: students.map(s => ({ id: s.id, name: s.name, period_id: s.period_id })),
        activePeriodId,
        manualFilter: allStudents.filter(s => s.period_id === activePeriodId).map(s => ({ id: s.id, name: s.name, period_id: s.period_id }))
      });

      // Debug: Check if students already have attendance data
      if (allStudents && allStudents.length > 0) {
        console.log('🔍 Checking if students already have status data...');
        const studentsWithStatus = allStudents.filter(s => s.status !== undefined);
        console.log('📊 Students with status:', studentsWithStatus.length, '/', allStudents.length);
        if (studentsWithStatus.length > 0) {
          console.log('📝 Sample student with status:', studentsWithStatus[0]);
          console.log('📋 All student status values:', studentsWithStatus.map(s => ({ id: s.id, name: s.name, status: s.status })));
          
          // If students already have status data, this might be an existing report
          if (studentsWithStatus.length === allStudents.length) {
            console.log('🎯 All students have status data - this might be pre-loaded attendance!');
            // Check if this is meaningful status data vs just undefined
            const meaningfulStatuses = studentsWithStatus.filter(s => s.status && s.status !== 'undefined');
            if (meaningfulStatuses.length > 0) {
              console.log('✅ Found meaningful status data - auto-filling from student data');
              const attendanceMap = {};
              const databaseStudentIds = new Set();
              studentsWithStatus.forEach(student => {
                if (student.status && student.status !== 'undefined') {
                  attendanceMap[student.id] = student.status.toLowerCase();
                  databaseStudentIds.add(student.id); // Track this student was from database
                }
              });
              console.log('📋 Student-based attendance map:', attendanceMap);
              setAttendance(attendanceMap);
              setStudentsFromDatabase(databaseStudentIds); // Track database students
              console.log('📊 Students loaded from database (pre-loaded):', Array.from(databaseStudentIds));
              setReportStatus('saved');
              setReportJustSubmitted(false); // This is loaded from database, not just submitted
              
              // For database auto-fill with saved reports, always set hadStudentsWithoutStatusOnLoad to true
              // if there are any currently enrolled students (they may or may not have status)
              setHadStudentsWithoutStatusOnLoad(studentsForThisPeriod.length > 0);
              console.log('📊 Pre-loaded - Setting hadStudentsWithoutStatusOnLoad to true for', studentsForThisPeriod.length, 'current students');
              
              // Continue to check for draft data for merging (don't return early)
              console.log('✅ Pre-loaded student data found, continuing to check for draft data to merge...');
            }
          }
        }
      }

      // Check auto-fill configuration
      const databaseAutoFillEnabled = isAutoFillFromDatabaseEnabled() && isAutoFillEnabled();
      const draftAutoFillEnabled = isAutoFillFromDraftsEnabled() && isAutoFillEnabled();
      
      console.log('⚙️ Auto-fill configuration check:', {
        isAutoFillFromDatabaseEnabled: isAutoFillFromDatabaseEnabled(),
        isAutoFillEnabled: isAutoFillEnabled(),
        databaseAutoFillEnabled,
        draftAutoFillEnabled
      });
      
      // If both auto-fill features are disabled, skip all checks immediately for better performance
      if (!databaseAutoFillEnabled && !draftAutoFillEnabled) {
        console.log('🚀 Auto-fill disabled - skipping database/draft checks for faster rendering');
        setHasAutoFilledData(false);
        setAutoFillMessage('');
        setHasDraftData(false);
        setDraftMessage('');
        setReportStatus('no-report');
        setHistoricalStudents([]); // Clear historical students when auto-fill disabled
        setAutoFillCheckComplete(true); // Mark check as complete immediately
        return;
      }

      // If no students for this period, check for drafts immediately (no database lookup needed)
      if (!studentsForThisPeriod.length) {
        console.log('📭 No students for this period - skipping to draft check');
        if (draftAutoFillEnabled) {
          const draftData = loadDraftAttendance(selectedClassId, activePeriodId, selectedDate);
          
          if (draftData && Object.keys(draftData.attendance).length > 0) {
            // Load draft attendance (will be applied when students load)
            setAttendance(draftData.attendance);
            setNotes(draftData.notes || {}); // Restore notes from draft
            setHasDraftData(true);
            setDraftMessage(`Restored unsaved changes from ${formatTimestampWithTimezone(new Date(draftData.timestamp))}`);
            setReportStatus('in-progress');
            
            console.log('📄 Loaded draft attendance for period with no current students:', draftData);
            
            // Auto-dismiss the message after 5 seconds
            setTimeout(() => {
              setHasDraftData(false);
              setDraftMessage('');
            }, 5000);
          } else {
            setHasAutoFilledData(false);
            setAutoFillMessage('');
            setHasDraftData(false);
            setDraftMessage('');
            setReportStatus('no-report');
          }
        } else {
          console.log('⚠️ Draft auto-fill disabled - skipping draft check');
          setHasAutoFilledData(false);
          setAutoFillMessage('');
          setHasDraftData(false);
          setDraftMessage('');
          setReportStatus('no-report');
          setHadStudentsWithoutStatusOnLoad(false); // No existing report, so reset flag
        }
        
        setAutoFillCheckComplete(true);
        return;
      }

      // If only draft auto-fill is enabled (no database check needed), handle synchronously
      if (!databaseAutoFillEnabled && draftAutoFillEnabled) {
        const draftData = loadDraftAttendance(selectedClassId, activePeriodId, selectedDate);
        
        if (draftData && Object.keys(draftData.attendance).length > 0) {
          // Set flag to prevent draft saving during draft data loading
          isUpdatingFromServerRef.current = true;
          
          // Load draft attendance
          setAttendance(draftData.attendance);
          setNotes(draftData.notes || {}); // Restore notes from draft
          setHasDraftData(true);
          setDraftMessage(`Restored unsaved changes from ${formatTimestampWithTimezone(new Date(draftData.timestamp))}`);
          setReportStatus('in-progress');
          
          console.log('📄 Loaded draft attendance:', draftData);
          
          // Reset the flag after a short delay
          setTimeout(() => {
            isUpdatingFromServerRef.current = false;
          }, 100);
          
          // Auto-dismiss the message after 5 seconds
          setTimeout(() => {
            setHasDraftData(false);
            setDraftMessage('');
          }, 5000);
        } else {
          setHasAutoFilledData(false);
          setAutoFillMessage('');
          setHasDraftData(false);
          setDraftMessage('');
          setReportStatus('no-report');
        }
        
        setAutoFillCheckComplete(true);
        return;
      }

      // Only proceed with async database check if database auto-fill is enabled
      try {
        const token = await getApiToken();
        
        // First, check if there's an existing attendance session in the database (if enabled)
        if (databaseAutoFillEnabled) {
          console.log('🔍 Checking for existing database attendance...');
          console.log('🔍 API call parameters:', {
            classId: selectedClassId,
            periodId: activePeriodId,
            date: selectedDate,
            classIdType: typeof selectedClassId,
            periodIdType: typeof activePeriodId
          });
          
          const existingSessions = await AttendanceAPI.getSessionsByClassFiltered(
            Number(selectedClassId), 
            {
              periodId: activePeriodId,
              date: selectedDate
            }, 
            token
          );

          console.log('📊 Database query result:', { existingSessions, sessionCount: existingSessions?.length || 0 });

          if (existingSessions && existingSessions.length > 0) {
            console.log('✅ Found existing session(s):', existingSessions);
            
            // Database record found - clear any draft data and use database data
            clearDraftAttendance(selectedClassId, activePeriodId, selectedDate);
            
            // Get the most recent session (in case there are multiple)
            const latestSession = existingSessions[existingSessions.length - 1];
            console.log('📋 Using session:', latestSession);
            
            // Fetch the student attendance records for this session
            const sessionStudents = await AttendanceAPI.getSessionStudents(latestSession.id, currentSchool.id, token);
            
            console.log('🔍 Raw session students data:', sessionStudents);
            console.log('🔍 First record structure:', sessionStudents[0]);
            console.log('🔍 All possible student ID fields:', {
              student_id: sessionStudents[0]?.student_id,
              studentId: sessionStudents[0]?.studentId,
              id: sessionStudents[0]?.id,
              T_ATT_STUDENT_id: sessionStudents[0]?.T_ATT_STUDENT?.id,
              all_keys: Object.keys(sessionStudents[0] || {})
            });
            
            // Extract student information and check for students not in current enrollment
            const currentStudentIds = new Set(allStudents.map(s => s.id));
            const historicalStudentsForThisDate = [];
            
            // Map the existing attendance to our attendance state
            const existingAttendance = {};
            const existingNotes = {};
            const databaseStudentIds = new Set(); // Track which students were loaded from database
            sessionStudents.forEach(record => {
              // The backend returns student ID as 'id' field
              const studentId = record.id;
              const status = (record.status || '').toLowerCase();
              const notes = record.notes || '';
              
              console.log('🔍 Mapping student attendance:', {
                studentId,
                originalStatus: record.status,
                lowercaseStatus: status,
                notes: notes,
                record
              });
              
              if (studentId) {
                existingAttendance[studentId] = status;
                databaseStudentIds.add(studentId); // Track this student was from database
                if (notes) {
                  existingNotes[studentId] = notes;
                }
                
                // Check if this student is not in current enrollment - these are historical students for THIS DATE
                if (!currentStudentIds.has(studentId)) {
                  console.log('📚 Found student in attendance record who is not currently enrolled:', {
                    studentId,
                    studentName: record.name || record.first + ' ' + record.last,
                    status
                  });
                  
                  // Create student object from attendance record data
                  const fullName = record.name || `${record.first || ''} ${record.last || ''}`.trim() || `Student ${studentId}`;
                  console.log('📚 Creating historical student for this date:', { 
                    studentId, 
                    recordName: record.name, 
                    firstName: record.first, 
                    lastName: record.last, 
                    fullName 
                  });
                  const historicalStudent = {
                    id: studentId,
                    student_id: studentId,
                    name: fullName,
                    first_name: record.first || '',
                    last_name: record.last || '',
                    email: record.email || '',
                    period_id: activePeriodId, // Associate with current period
                    avatar: record.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}`,
                    isHistoricalRecord: true, // Flag to indicate this student is from historical attendance
                    status: status // Store the attendance status
                  };
                  
                  historicalStudentsForThisDate.push(historicalStudent);
                }
              }
            });
            
            // Store historical students for this specific date in state instead of adding to allStudents
            setHistoricalStudents(historicalStudentsForThisDate);
            
            // Track for draft-disabled logic
            historicalStudentsFound = historicalStudentsForThisDate;
            
            console.log('🔍 Final existingAttendance object:', existingAttendance);
            console.log('🔍 Final existingNotes object:', existingNotes);
            console.log('🔍 Current students array:', students.map(s => ({ id: s.id, name: s.name })));
            
            // Store loaded data for later check
            loadedAttendanceData = existingAttendance;
            
            // Set flag to prevent draft saving during server data loading
            isUpdatingFromServerRef.current = true;
            
            // Update attendance state with existing records
            setAttendance(prevAttendance => {
              const newAttendance = {
                ...prevAttendance,
                ...existingAttendance
              };
              console.log('🔍 Updated attendance state:', newAttendance);
              return newAttendance;
            });

            // Update notes state with existing notes
            setNotes(existingNotes); // Replace notes entirely instead of merging
            
            // Track which students were loaded from database for note section categorization
            setStudentsFromDatabase(databaseStudentIds);
            console.log('📊 Students loaded from database:', Array.from(databaseStudentIds));
            
            // Set auto-fill indicators
            setHasAutoFilledData(true);
            setAutoFillMessage(`Loaded existing attendance from ${formatTimestampWithTimezone(new Date(latestSession.updated_at || latestSession.created_at || latestSession.date))}`);
            setReportStatus('saved');
            setReportJustSubmitted(false); // This is loaded from database, not just submitted
            
            // For database auto-fill with saved reports, always set hadStudentsWithoutStatusOnLoad to true
            // if there are any currently enrolled students (they may or may not have status)
            // This ensures the note separator appears when there's existing database data
            setHadStudentsWithoutStatusOnLoad(studentsForThisPeriod.length > 0);
            console.log('📊 Database auto-fill - Setting hadStudentsWithoutStatusOnLoad to true for', studentsForThisPeriod.length, 'current students');
            
            console.log('✅ Auto-filled attendance from existing session:', latestSession.id);
            
            // Reset the flag after a short delay to allow state updates to complete
            setTimeout(() => {
              isUpdatingFromServerRef.current = false;
            }, 100);
            
            // Auto-dismiss the message after 5 seconds
            setTimeout(() => {
              setHasAutoFilledData(false);
              setAutoFillMessage('');
            }, 5000);
            
            // Continue to check for draft data for merging (don't return early)
            console.log('✅ Database data loaded, continuing to check for draft data to merge...');
          } else {
            console.log('📭 No existing attendance sessions found in database');
            
            // Fallback: Check if there are any attendance records for these students on this date
            // even if they're not properly associated with a session
            console.log('🔍 Checking for individual attendance records as fallback...');
            try {
              // Try using the bulk API endpoint with current parameters to see if data exists
              const fallbackCheck = await AttendanceAPI.getSessionsBySchoolFiltered(
                currentSchool?.id || currentSchool?.unique_id, // Use the current school ID
                {
                  classId: selectedClassId,
                  periodId: activePeriodId,
                  date: selectedDate
                },
                token
              );
              
              console.log('🔄 Fallback check result:', fallbackCheck);
              
              if (fallbackCheck && fallbackCheck.length > 0) {
                console.log('✅ Found data via fallback method!');
                const fallbackSession = fallbackCheck[0];
                console.log('📋 Using fallback session:', fallbackSession);
                
                const attendanceStudents = await AttendanceAPI.getSessionStudents(fallbackSession.id, currentSchool.id, token);
                console.log('👥 Fallback session students:', attendanceStudents);
                
                if (attendanceStudents && attendanceStudents.length > 0) {
                  // Extract student information and check for students not in current enrollment (fallback case)
                  const currentStudentIds = new Set(allStudents.map(s => s.id));
                  const studentsFromFallbackAttendance = [];
                  
                  const attendanceMap = {};
                  const notesMap = {};
                  const databaseStudentIds = new Set(); // Track which students were from database
                  attendanceStudents.forEach(record => {
                    console.log('📝 Processing fallback record:', { studentId: record.id, status: record.status, notes: record.notes, record });
                    attendanceMap[record.id] = (record.status || '').toLowerCase();
                    databaseStudentIds.add(record.id); // Track this student was from database
                    if (record.notes) {
                      notesMap[record.id] = record.notes;
                    }
                    
                    // Check if this student is not in current enrollment (fallback case)
                    if (!currentStudentIds.has(record.id)) {
                      console.log('📚 Found student in fallback attendance record who is not currently enrolled:', {
                        studentId: record.id,
                        studentName: record.name || record.first + ' ' + record.last,
                        status: record.status
                      });
                      
                      // Create student object from attendance record data
                      const fullName = record.name || `${record.first || ''} ${record.last || ''}`.trim() || `Student ${record.id}`;
                      console.log('📚 Creating fallback historical student:', { 
                        studentId: record.id, 
                        recordName: record.name, 
                        firstName: record.first, 
                        lastName: record.last, 
                        fullName 
                      });
                      const historicalStudent = {
                        id: record.id,
                        student_id: record.id,
                        name: fullName,
                        first_name: record.first || '',
                        last_name: record.last || '',
                        email: record.email || '',
                        period_id: activePeriodId, // Associate with current period
                        avatar: record.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}`,
                        isHistoricalRecord: true, // Flag to indicate this student is from historical attendance
                        status: (record.status || '').toLowerCase() // Store the attendance status
                      };
                      
                      studentsFromFallbackAttendance.push(historicalStudent);
                    }
                  });
                  
                  // Add historical students to state if any were found (fallback case)
                  if (studentsFromFallbackAttendance.length > 0) {
                    console.log('📚 Adding', studentsFromFallbackAttendance.length, 'historical students from fallback attendance records');
                    setHistoricalStudents(studentsFromFallbackAttendance);
                    
                    // Track for draft-disabled logic
                    historicalStudentsFound = studentsFromFallbackAttendance;
                    
                    console.log('📚 Set historical students from fallback:', studentsFromFallbackAttendance.length, 'students');
                  }
                  
                  console.log('📋 Fallback attendance map:', attendanceMap);
                  console.log('📋 Fallback notes map:', notesMap);
                  setAttendance(attendanceMap);
                  setNotes(notesMap);
                  setStudentsFromDatabase(databaseStudentIds); // Track database students
                  console.log('📊 Students loaded from database (fallback):', Array.from(databaseStudentIds));
                  setReportStatus('saved');
                  setReportJustSubmitted(false); // This is loaded from database, not just submitted
                  
                  // For database auto-fill with saved reports, always set hadStudentsWithoutStatusOnLoad to true
                  // if there are any currently enrolled students (they may or may not have status)
                  setHadStudentsWithoutStatusOnLoad(studentsForThisPeriod.length > 0);
                  console.log('📊 Fallback - Setting hadStudentsWithoutStatusOnLoad to true for', studentsForThisPeriod.length, 'current students');
                  
                  // Continue to check for draft data for merging (don't return early)
                  console.log('✅ Fallback database data loaded, continuing to check for draft data to merge...');
                }
              }
            } catch (fallbackError) {
              console.log('⚠️ Fallback check failed:', fallbackError);
            }
          }
        } else {
          console.log('⚠️ Database auto-fill disabled - skipping database check');
        }
        
        // No database record found - check for draft data
        if (draftAutoFillEnabled) {
          const draftData = loadDraftAttendance(selectedClassId, activePeriodId, selectedDate);
          
          if (draftData && Object.keys(draftData.attendance).length > 0) {
            // Set flag to prevent draft saving during draft data loading
            isUpdatingFromServerRef.current = true;
            
            // Merge draft attendance with existing database attendance (draft takes priority)
            setAttendance(prevAttendance => {
              const mergedAttendance = {
                ...prevAttendance, // Database data (lower priority)
                ...draftData.attendance // Draft data (higher priority - overwrites database)
              };
              console.log('🔄 Merging attendance data:', {
                databaseData: prevAttendance,
                draftData: draftData.attendance,
                merged: mergedAttendance
              });
              return mergedAttendance;
            });
            
            // Merge draft notes with existing database notes (draft takes priority)
            setNotes(prevNotes => {
              const mergedNotes = {
                ...prevNotes, // Database notes (lower priority)
                ...(draftData.notes || {}) // Draft notes (higher priority)
              };
              console.log('🔄 Merging notes data:', {
                databaseNotes: prevNotes,
                draftNotes: draftData.notes,
                merged: mergedNotes
              });
              return mergedNotes;
            });
            
            setHasDraftData(true);
            setDraftMessage(`Restored unsaved changes from ${formatTimestampWithTimezone(new Date(draftData.timestamp))}`);
            
            // Set appropriate status based on whether database data was also found
            if (reportStatus === 'saved' || hasAutoFilledData) {
              // Database data exists + draft data exists = in-progress with database base
              setReportStatus('in-progress');
              console.log('📊 Combined state: Database data + Draft changes = in-progress');
            } else {
              // Only draft data exists = pure in-progress
              setReportStatus('in-progress');
              console.log('📊 Draft-only state: in-progress');
            }
            
            console.log('📄 Merged draft with database attendance:', draftData);
            
            // Reset the flag after a short delay
            setTimeout(() => {
              isUpdatingFromServerRef.current = false;
            }, 100);
            
            // Auto-dismiss the message after 5 seconds
            setTimeout(() => {
              setHasDraftData(false);
              setDraftMessage('');
            }, 5000);
          } else {
            // No draft data found - only reset draft-specific states, preserve database data if it exists
            console.log('📭 No draft data found');
            setHasDraftData(false);
            setDraftMessage('');
            // Don't reset hasAutoFilledData, autoFillMessage, or reportStatus if database data was already loaded
          }
        } else {
          console.log('⚠️ Draft auto-fill disabled - skipping draft check');
          // Only reset states if no database data was loaded either
          const hasAttendanceData = Object.keys(attendance).length > 0;
          const hasLoadedData = Object.keys(loadedAttendanceData).length > 0;
          const hasDatabaseData = reportStatus === 'saved' || hasAutoFilledData || hasAttendanceData || hasLoadedData;
          const hasHistoricalStudents = historicalStudentsFound.length > 0;
          
          console.log('🔍 Draft disabled check:', {
            hasAttendanceData,
            hasLoadedData,
            hasDatabaseData,
            hasHistoricalStudents,
            reportStatus,
            hasAutoFilledData,
            attendanceDataCount: Object.keys(attendance).length,
            loadedDataCount: Object.keys(loadedAttendanceData).length,
            historicalStudentsCount: historicalStudentsFound.length,
            shouldPreserveFlag: hasDatabaseData || hasHistoricalStudents
          });
          
          if (!hasDatabaseData && !hasHistoricalStudents) {
            setHasAutoFilledData(false);
            setAutoFillMessage('');
            setReportStatus('no-report');
            setHadStudentsWithoutStatusOnLoad(false); // No existing report, so reset flag
            console.log('🔄 No database data or historical students - resetting hadStudentsWithoutStatusOnLoad');
          } else {
            console.log('✅ Preserving hadStudentsWithoutStatusOnLoad - database data or historical students exist');
          }
          setHasDraftData(false);
          setDraftMessage('');
        }
      } catch (err) {
        console.error('Failed to load existing attendance:', err);
        setHasAutoFilledData(false);
        setAutoFillMessage('');
        setReportStatus('no-report');
        // Don't show error to user as this is optional functionality
      }
      
      // Final check: if neither database nor draft data was found, ensure proper state reset
      const finalDatabaseAutoFillEnabled = isAutoFillFromDatabaseEnabled() && isAutoFillEnabled();
      const finalDraftAutoFillEnabled = isAutoFillFromDraftsEnabled() && isAutoFillEnabled();
      
      console.log('🔍 Final check debug:', {
        finalDatabaseAutoFillEnabled,
        finalDraftAutoFillEnabled,
        reportStatus,
        hasAutoFilledData,
        hasDraftData,
        hadStudentsWithoutStatusOnLoad,
        shouldReset: finalDatabaseAutoFillEnabled && finalDraftAutoFillEnabled && reportStatus === 'no-report' && !hasAutoFilledData && !hasDraftData
      });
      
      if (finalDatabaseAutoFillEnabled && finalDraftAutoFillEnabled && reportStatus === 'no-report' && !hasAutoFilledData && !hasDraftData) {
        console.log('📭 No data found in either database or drafts - resetting to no-report state');
        setReportStatus('no-report');
        setHadStudentsWithoutStatusOnLoad(false);
      }
      
      // Mark auto-fill check as complete regardless of success/failure
      setAutoFillCheckComplete(true);
    };

    loadExistingAttendance();
  }, [selectedClassId, activePeriodId, selectedDate, autoFillCheckComplete, loadingStudents, allStudents, currentSchool]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle period changes with complete state reset
  const handlePeriodChange = useCallback((periodId) => {
    // Reset all attendance-related state
    setAttendance({});
    setNotes({}); // Clear notes when period changes
    setHistoricalStudents([]); // Clear historical students when period changes
    setHasAutoFilledData(false);
    setAutoFillMessage('');
    setHasDraftData(false);
    setDraftMessage('');
    setCurrentPage(1);
    setSearch('');
    setReportJustSubmitted(false); // Reset submitted flag when period changes
    setOriginalStudentCategories({}); // Reset student categories for new period
    setStudentsFromDatabase(new Set()); // Reset database tracking for new period
    
    // Check if any auto-fill features are enabled to determine if we need the loading state
    const databaseAutoFillEnabled = isAutoFillFromDatabaseEnabled() && isAutoFillEnabled();
    const draftAutoFillEnabled = isAutoFillFromDraftsEnabled() && isAutoFillEnabled();
    
    // Set initial report status based on whether we'll be checking for reports
    if (databaseAutoFillEnabled || draftAutoFillEnabled) {
      setReportStatus('checking'); // Show checking immediately if we'll be doing auto-fill
      setAutoFillCheckComplete(false);
    } else {
      setReportStatus('no-report'); // Only show no-report if auto-fill is disabled
      setAutoFillCheckComplete(true);
    }
    
    // Set the new period
    setActivePeriodId(periodId);
  }, []);

  // Memoized filtered students for better performance
  const filteredStudents = useMemo(() => {
    console.log('🔍 FilteredStudents memo running:', {
      studentsCount: students.length,
      studentsData: students.map(s => ({ id: s.id, name: s.name })),
      search: search
    });
    
    const filtered = students.filter(s => 
      (s.name || '').toLowerCase().includes((search || '').toLowerCase())
    );
    
    console.log('🔍 After search filter:', {
      beforeFilter: students.length,
      afterFilter: filtered.length,
      searchTerm: search
    });
    
    // Sort students: current students first, then historical students
    // BUT preserve original order within each group (don't sort alphabetically)
    const sorted = filtered.sort((a, b) => {
      // If one is historical and one is not, non-historical comes first
      if (a.isHistoricalRecord && !b.isHistoricalRecord) return 1;
      if (!a.isHistoricalRecord && b.isHistoricalRecord) return -1;
      
      // If both are the same type (both historical or both current), maintain original order
      // Use the original index from the students array to maintain enrollment order
      const aIndex = students.findIndex(s => s.id === a.id);
      const bIndex = students.findIndex(s => s.id === b.id);
      console.log(`🔍 Base sort: ${a.name} (index ${aIndex}) vs ${b.name} (index ${bIndex})`);
      return aIndex - bIndex;
    });
    
    console.log('🔍 Final filteredStudents result:', {
      count: sorted.length,
      data: sorted.map(s => ({ id: s.id, name: s.name })),
      originalStudentsOrder: students.map(s => ({ id: s.id, name: s.name }))
    });
    
    return sorted;
  }, [students, search]);

  // Use filtered students with clear separation between different features
  const studentSections = useMemo(() => {
    const databaseAutoFillEnabled = isAutoFillFromDatabaseEnabled() && isAutoFillEnabled();
    
    console.log('🔍 StudentSections debug:', {
      filteredStudentsCount: filteredStudents.length,
      filteredStudentsData: filteredStudents.map(s => ({ id: s.id, name: s.name, isHistoricalRecord: s.isHistoricalRecord })),
      search: search,
      originalCategories: originalStudentCategories
    });
    
    // Filter only current students (not historical) from the main student list
    const currentStudents = filteredStudents.filter(s => !s.isHistoricalRecord);
    
    // Use historical students from state (which are date-specific)
    const dateSpecificHistoricalStudents = historicalStudents.filter(s => 
      (s.name || '').toLowerCase().includes((search || '').toLowerCase())
    );
    
    // Create separate sections based on original categorization or determine initial categorization
    const sections = {
      historicalStudents: [],
      noteSection: null,
      regularStudents: [],
      allStudents: [] // Combined for pagination (backwards compatibility)
    };
    
    // If we don't have original categories set yet, OR if database data has changed and we need to recategorize
    // BUT only after auto-fill check is complete to ensure we have all database data
    const needsRecategorization = (
      (Object.keys(originalStudentCategories).length === 0 && currentStudents.length > 0 && autoFillCheckComplete) ||
      (studentsFromDatabase.size > 0 && autoFillCheckComplete && !reportJustSubmitted && 
       currentStudents.some(s => !studentsFromDatabase.has(s.id) && originalStudentCategories[s.id] === 'regular'))
    );
    
    if (needsRecategorization) {
      console.log('🔧 Setting/updating student categories...');
      const newCategories = {};
      
      // Check if we have database students (indicates a saved report was loaded)
      const hasDatabaseStudents = studentsFromDatabase.size > 0;
      console.log('📊 Database check:', { 
        hasDatabaseStudents, 
        studentsFromDatabaseCount: studentsFromDatabase.size,
        reportStatus,
        databaseAutoFillEnabled,
        autoFillCheckComplete
      });
      
      if (databaseAutoFillEnabled && hasDatabaseStudents && !reportJustSubmitted) {
        // When we have a saved report from database, categorize based on who was in the original report
        console.log('📊 Categorizing with database data. Students from DB:', Array.from(studentsFromDatabase));
        
        currentStudents.forEach(student => {
          // Students who were NOT in the database report go to note section
          // Students who WERE in the database report go to regular section
          if (studentsFromDatabase.has(student.id)) {
            newCategories[student.id] = 'regular';
            console.log(`✅ Student ${student.name} (${student.id}) -> REGULAR (was in database)`);
          } else {
            newCategories[student.id] = 'note';
            console.log(`📝 Student ${student.name} (${student.id}) -> NOTE (not in database)`);
          }
        });
      } else {
        // All other cases: all current students go to regular section
        console.log('📊 No saved report - all students go to regular section');
        currentStudents.forEach(student => {
          newCategories[student.id] = 'regular';
        });
      }
      
      // Set the categories (this will trigger a re-render)
      setOriginalStudentCategories(newCategories);
      console.log('📝 Categories set/updated:', newCategories);
    }
    
    // Use original categories to determine student placement (static positioning)
    const regularStudentsList = [];
    const noteStudentsList = [];
    
    console.log('🔍 Student categorization debug:', {
      originalCategoriesCount: Object.keys(originalStudentCategories).length,
      currentStudentsCount: currentStudents.length,
      autoFillCheckComplete,
      reportStatus,
      studentsFromDatabaseSize: studentsFromDatabase.size
    });
    
    // If we don't have categories yet, sort all students by their original order and put in regular section
    if (Object.keys(originalStudentCategories).length === 0) {
      console.log('⚠️ No categories set yet - using default sorting for all students');
      console.log('🔍 Current students before sorting:', currentStudents.map(s => ({ id: s.id, name: s.name })));
      console.log('🔍 Filtered students order:', filteredStudents.map(s => ({ id: s.id, name: s.name })));
      
      // Sort current students by their index in filteredStudents to maintain consistent order
      const sortedCurrentStudents = currentStudents.sort((a, b) => {
        const aIndex = filteredStudents.findIndex(s => s.id === a.id);
        const bIndex = filteredStudents.findIndex(s => s.id === b.id);
        console.log(`🔍 Sorting: ${a.name} (index ${aIndex}) vs ${b.name} (index ${bIndex})`);
        return aIndex - bIndex;
      });
      
      console.log('🔍 Students after default sorting:', sortedCurrentStudents.map(s => ({ id: s.id, name: s.name })));
      regularStudentsList.push(...sortedCurrentStudents);
    } else {
      console.log('✅ Using categories for student placement');
      console.log('🔍 Categories:', originalStudentCategories);
      
      // Use categories to determine placement
      currentStudents.forEach(student => {
        const category = originalStudentCategories[student.id] || 'regular';
        if (category === 'note') {
          noteStudentsList.push(student);
          console.log(`📝 ${student.name} -> NOTE section`);
        } else {
          regularStudentsList.push(student);
          console.log(`✅ ${student.name} -> REGULAR section`);
        }
      });
    }
    
    console.log('🔍 Before final sorting - Regular students:', regularStudentsList.map(s => ({ id: s.id, name: s.name })));
    console.log('🔍 Before final sorting - Note students:', noteStudentsList.map(s => ({ id: s.id, name: s.name })));
    
    // Sort students within their sections to maintain original order
    const sortedRegularStudents = regularStudentsList.sort((a, b) => {
      const aIndex = filteredStudents.findIndex(s => s.id === a.id);
      const bIndex = filteredStudents.findIndex(s => s.id === b.id);
      console.log(`🔍 Final regular sort: ${a.name} (index ${aIndex}) vs ${b.name} (index ${bIndex})`);
      return aIndex - bIndex;
    });
    
    const sortedNoteStudents = noteStudentsList.sort((a, b) => {
      const aIndex = filteredStudents.findIndex(s => s.id === a.id);
      const bIndex = filteredStudents.findIndex(s => s.id === b.id);
      console.log(`🔍 Final note sort: ${a.name} (index ${aIndex}) vs ${b.name} (index ${bIndex})`);
      return aIndex - bIndex;
    });
    
    console.log('🔍 After final sorting - Regular students:', sortedRegularStudents.map(s => ({ id: s.id, name: s.name })));
    console.log('🔍 After final sorting - Note students:', sortedNoteStudents.map(s => ({ id: s.id, name: s.name })));
    
    if (databaseAutoFillEnabled) {
      // Historical students section
      if (dateSpecificHistoricalStudents.length > 0) {
        sections.historicalStudents = dateSpecificHistoricalStudents;
        sections.allStudents.push({
          id: '__historical_separator__',
          isHistoricalSeparator: true,
          historicalStudentCount: dateSpecificHistoricalStudents.length
        });
        sections.allStudents.push(...dateSpecificHistoricalStudents);
      }
      
      // Regular students section
      if (sortedRegularStudents.length > 0) {
        sections.regularStudents = sortedRegularStudents;
        sections.allStudents.push(...sections.regularStudents);
      }
      
      // Note section for students (only if there are note students)
      if (sortedNoteStudents.length > 0) {
        sections.noteSection = {
          studentsWithoutStatusCount: sortedNoteStudents.length,
          studentsWithoutStatus: enableUnselectedStudents ? sortedNoteStudents : []
        };
        
        // For backwards compatibility
        sections.allStudents.push({ 
          id: '__note_separator__', 
          isNoteSeparator: true, 
          studentsWithoutStatusCount: sortedNoteStudents.length
        });
        if (enableUnselectedStudents) {
          sections.allStudents.push(...sections.noteSection.studentsWithoutStatus);
        }
      }
    } else {
      // When autofill is disabled: show ALL current students in regular section
      sections.regularStudents = [...sortedRegularStudents, ...sortedNoteStudents].sort((a, b) => {
        const aIndex = filteredStudents.findIndex(s => s.id === a.id);
        const bIndex = filteredStudents.findIndex(s => s.id === b.id);
        return aIndex - bIndex;
      });
      sections.allStudents = [...sections.regularStudents];
      
      // Add historical students section if we have any
      if (dateSpecificHistoricalStudents.length > 0) {
        sections.historicalStudents = dateSpecificHistoricalStudents;
        sections.allStudents.push({
          id: '__historical_separator__',
          isHistoricalSeparator: true,
          historicalStudentCount: dateSpecificHistoricalStudents.length
        });
        sections.allStudents.push(...dateSpecificHistoricalStudents);
      }
    }
    
    console.log('🔍 Final sections result:', {
      historicalCount: sections.historicalStudents.length,
      regularCount: sections.regularStudents.length,
      noteSection: sections.noteSection ? `${sections.noteSection.studentsWithoutStatusCount} students` : 'none',
      allStudentsCount: sections.allStudents.length,
      categoriesInUse: Object.keys(originalStudentCategories).length
    });
    
    return sections;
  }, [filteredStudents, historicalStudents, search, enableUnselectedStudents, reportStatus, reportJustSubmitted, originalStudentCategories, setOriginalStudentCategories, studentsFromDatabase, autoFillCheckComplete]);

  // Create displayStudents for backwards compatibility with pagination
  const displayStudents = studentSections.allStudents;

  // Memoized pagination logic
  const { totalPages, paginatedStudents, studentsPerPage } = useMemo(() => {
    const perPage = 11;
    const total = Math.ceil(displayStudents.length / perPage);
    const paginated = displayStudents.slice(
      (currentPage - 1) * perPage, 
      currentPage * perPage
    );
    return { totalPages: total, paginatedStudents: paginated, studentsPerPage: perPage };
  }, [displayStudents, currentPage]);

  // Optimized bulk operations with useCallback
  const clearAll = useCallback(() => {
    setAttendance({});
    setNotes({}); // Clear all notes as well
    setHistoricalStudents([]); // Clear historical students as well
    // Note: Don't reset reportStatus here - it should reflect database state, not UI state
  }, []);

  const markAllPresent = useCallback(() => {
    const newAttendance = {};
    students.forEach(s => { newAttendance[s.id] = 'present'; });
    setAttendance(newAttendance);
  }, [students]);

  const markAllLate = useCallback(() => {
    const newAttendance = {};
    students.forEach(s => { newAttendance[s.id] = 'late'; });
    setAttendance(newAttendance);
  }, [students]);

  const markAllAbsent = useCallback(() => {
    const newAttendance = {};
    students.forEach(s => { newAttendance[s.id] = 'absent'; });
    setAttendance(newAttendance);
  }, [students]);

  const markAllExcused = useCallback(() => {
    const newAttendance = {};
    students.forEach(s => { newAttendance[s.id] = 'excused'; });
    setAttendance(newAttendance);
  }, [students]);

  // Handler for adding a new student
  const handleAddStudent = useCallback(() => {
    setShowAddStudentModal(true);
  }, []);

  // Handler for closing add student modal
  const handleCloseAddStudentModal = useCallback(() => {
    setShowAddStudentModal(false);
  }, []);

  // Handler for when a new student is created
  const handleNewStudentCreated = useCallback((newStudentData) => {
    if (!newStudentData || !activePeriodId) return;
    
    console.log('  handleNewStudentCreated called with:', newStudentData);
    
    // Save current attendance state before any updates
    const currentAttendance = { ...attendance };
    
    // Handle both single student and array of students
    const studentsToAdd = Array.isArray(newStudentData) ? newStudentData : [newStudentData];
    
    console.log('  New students to add:', studentsToAdd);

    // Format the new students with the same structure as existing students
    const formattedNewStudents = studentsToAdd.map(student => {
      const fullName = `${student.first_name || ''} ${student.last_name || ''}`.trim();
      const formatted = {
        ...student,
        period_id: activePeriodId, // Ensure they have the correct period_id
        name: fullName,
        avatar: student.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}`,
      };
      
      console.log('  Formatted new student:', formatted);
      return formatted;
    });

    // Update allStudents state (the useEffect will automatically update students via filtering)
    setAllStudents(prevAllStudents => {
      // Check if any of the students already exist (avoid duplicates)
      const existingIds = new Set(prevAllStudents.map(s => s.id || s.student_id));
      const newStudents = formattedNewStudents.filter(s => !existingIds.has(s.id || s.student_id));
      
      console.log('  Adding to allStudents state:', newStudents);
      console.log('  Previous allStudents count:', prevAllStudents.length);
      
      return [...prevAllStudents, ...newStudents];
    });

    // Update categories for newly created students
    setOriginalStudentCategories(prevCategories => {
      const updatedCategories = { ...prevCategories };
      
      formattedNewStudents.forEach(newStudent => {
        const studentId = newStudent.id || newStudent.student_id;
        // Only add category if it doesn't already exist
        if (!updatedCategories[studentId]) {
          updatedCategories[studentId] = 'regular';
          console.log('  Added category for new student:', studentId, 'as regular');
        }
      });
      
      console.log('  Updated categories with new students');
      return updatedCategories;
    });
    
    // Store attendance data for preservation
    setAttendanceBackup(currentAttendance);
    
    // Clear cache so future loads get fresh data
    clearClassDataCache(selectedClassId);
  }, [activePeriodId, selectedClassId, clearClassDataCache, attendance, setOriginalStudentCategories]);

  // Handler for when existing students are enrolled
  const handleExistingStudentsEnrolled = useCallback((enrolledStudentData) => {
    if (!enrolledStudentData || !activePeriodId) return;
    
    console.log('📚 handleExistingStudentsEnrolled called with:', enrolledStudentData);
    
    // Save current attendance state before any updates
    const currentAttendance = { ...attendance };
    
    // Handle both single student and array of students
    const studentsToAdd = Array.isArray(enrolledStudentData) ? enrolledStudentData : [enrolledStudentData];
    
    console.log('📚 Existing students to enroll:', studentsToAdd);

    // Format the enrolled students with the same structure as existing students
    const formattedEnrolledStudents = studentsToAdd.map(student => {
      const fullName = `${student.first_name || ''} ${student.last_name || ''}`.trim();
      const formatted = {
        ...student,
        period_id: activePeriodId, // Ensure they have the correct period_id
        name: fullName,
        avatar: student.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}`,
        // Ensure consistent ID field naming
        id: student.id || student.student_id,
        student_id: student.student_id || student.id
      };
      
      console.log('📚 Formatted enrolled student:', formatted);
      return formatted;
    });

    // Update allStudents state (the useEffect will automatically update students via filtering)
    setAllStudents(prevAllStudents => {
      // For existing student enrollment, we need to check if the student is already in allStudents
      // but just not enrolled in this period. If so, we need to update their period enrollment
      const existingStudentsMap = new Map(prevAllStudents.map(s => [(s.id || s.student_id), s]));
      let updatedAllStudents = [...prevAllStudents];
      
      formattedEnrolledStudents.forEach(enrolledStudent => {
        const studentId = enrolledStudent.id || enrolledStudent.student_id;
        
        if (existingStudentsMap.has(studentId)) {
          // Student exists but may not be enrolled in this period - we need to add them for this period
          // Check if they're already enrolled in this specific period
          const isAlreadyInPeriod = prevAllStudents.some(s => 
            (s.id || s.student_id) === studentId && s.period_id === activePeriodId
          );
          
          if (!isAlreadyInPeriod) {
            // Add the student for this specific period
            updatedAllStudents.push(enrolledStudent);
          }
        } else {
          // Student doesn't exist in allStudents at all, add them
          updatedAllStudents.push(enrolledStudent);
        }
      });
      
      console.log('📚 Updated allStudents with enrolled students');
      console.log('📚 Previous allStudents count:', prevAllStudents.length);
      console.log('📚 New allStudents count:', updatedAllStudents.length);
      
      return updatedAllStudents;
    });

    // Update categories for newly added students
    setOriginalStudentCategories(prevCategories => {
      const updatedCategories = { ...prevCategories };
      
      formattedEnrolledStudents.forEach(enrolledStudent => {
        const studentId = enrolledStudent.id || enrolledStudent.student_id;
        // Only add category if it doesn't already exist
        if (!updatedCategories[studentId]) {
          updatedCategories[studentId] = 'regular';
          console.log('📚 Added category for new student:', studentId, 'as regular');
        }
      });
      
      console.log('📚 Updated categories with new students');
      return updatedCategories;
    });
    
    // Store attendance data for preservation
    setAttendanceBackup(currentAttendance);
    
    // Clear cache so future loads get fresh data
    clearClassDataCache(selectedClassId);
  }, [activePeriodId, selectedClassId, clearClassDataCache, attendance, setOriginalStudentCategories]);  // Browser storage utilities for draft attendance
  const getDraftStorageKey = useCallback((classId, periodId, date) => {
    // Include user identifier to avoid conflicts on shared devices
    const userKey = user?.sub || 'anonymous';
    return `arcattend_draft_${userKey}_${classId}_${periodId}_${date}`;
  }, [user?.sub]);

  const saveDraftAttendance = useCallback((attendance, notes, classId, periodId, date) => {
    try {
      const storageKey = getDraftStorageKey(classId, periodId, date);
      const draftData = {
        attendance,
        notes,
        timestamp: Date.now(),
        classId,
        periodId,
        date,
        version: '1.0' // For future compatibility
      };
      localStorage.setItem(storageKey, JSON.stringify(draftData));
      console.log('💾 Saved draft attendance to browser storage:', storageKey);
    } catch (error) {
      console.warn('Failed to save draft attendance:', error);
    }
  }, [getDraftStorageKey]);

  const loadDraftAttendance = useCallback((classId, periodId, date) => {
    try {
      const storageKey = getDraftStorageKey(classId, periodId, date);
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) return null;
      
      const draftData = JSON.parse(stored);
      
      // Check if data is too old (24 hours)
      const isExpired = Date.now() - draftData.timestamp > 24 * 60 * 60 * 1000;
      if (isExpired) {
        localStorage.removeItem(storageKey);
        console.log('🗑️ Removed expired draft attendance:', storageKey);
        return null;
      }
      
      // Validate data structure
      if (!draftData.attendance || !draftData.classId || !draftData.periodId || !draftData.date) {
        localStorage.removeItem(storageKey);
        console.log('🗑️ Removed corrupted draft attendance:', storageKey);
        return null;
      }
      
      console.log('📂 Loaded draft attendance from browser storage:', storageKey);
      return draftData;
    } catch (error) {
      console.warn('Failed to load draft attendance:', error);
      return null;
    }
  }, [getDraftStorageKey]);



  const clearDraftAttendance = useCallback((classId, periodId, date) => {
    try {
      const storageKey = getDraftStorageKey(classId, periodId, date);
      localStorage.removeItem(storageKey);
      console.log('🗑️ Cleared draft attendance from browser storage:', storageKey);
    } catch (error) {
      console.warn('Failed to clear draft attendance:', error);
    }
  }, [getDraftStorageKey]);

  const cleanupOldDrafts = useCallback(() => {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('arcattend_draft_')) {
          try {
            const stored = localStorage.getItem(key);
            const data = JSON.parse(stored);
            const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
            if (isExpired) {
              keysToRemove.push(key);
            }
          } catch {
            keysToRemove.push(key); // Remove corrupted entries
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      if (keysToRemove.length > 0) {
        console.log(`🗑️ Cleaned up ${keysToRemove.length} old draft entries`);
      }
    } catch (error) {
      console.warn('Failed to cleanup old drafts:', error);
    }
  }, []);

  // Toast management function
  const showSuccessToast = useCallback((message) => {
    setToast({ message, isVisible: true, zIndex: 9999 }); // High z-index when active
  }, []);

  // Handler for deleting the entire attendance report
  const handleDeleteReport = useCallback(async () => {
    if (!selectedClassId || !selectedDate) {
      setErrorMessage('Missing class or date information');
      setShowErrorToast(true);
      return;
    }

    // Show confirmation modal instead of window.confirm
    setShowDeleteConfirmModal(true);
  }, [selectedClassId, selectedDate]);

  const handleConfirmDeleteReport = useCallback(async () => {
    try {
      const token = await getApiToken();
      
      // Use the AttendanceAPI deleteSession method
      await AttendanceAPI.deleteSession(selectedClassId, selectedDate, activePeriodId, token);

      // Success - clear local data and update UI
      setAttendance({});
      setReportStatus('no-report');
      clearDraftAttendance(selectedClassId, activePeriodId, selectedDate);
      
      showSuccessToast('Attendance report deleted successfully!');

      // Close the confirmation modal
      setShowDeleteConfirmModal(false);

    } catch (err) {
      console.error('Error deleting attendance report:', err);
      
      // Handle specific error cases
      if (err.error === 'Attendance session not found') {
        setErrorMessage('No attendance report found to delete');
      } else {
        setErrorMessage('Failed to delete attendance report. Please try again.');
      }
      setShowErrorToast(true);
      setShowDeleteConfirmModal(false);
    }
  }, [selectedClassId, activePeriodId, selectedDate, getApiToken, clearDraftAttendance, showSuccessToast]);



  // Cleanup old draft attendance on component mount
  useEffect(() => {
    cleanupOldDrafts();
  }, [cleanupOldDrafts]);

  // 6) Save draft attendance to browser storage when attendance changes
  useEffect(() => {
    // Don't save if we're currently updating from server/draft data
    if (isUpdatingFromServerRef.current) {
      return;
    }
    
    // Check if draft auto-fill (including saving) is enabled
    if (!isAutoFillFromDraftsEnabled() || !isAutoFillEnabled()) {
      return;
    }
    
    // Don't save drafts if report is already saved in database
    if (reportStatus === 'saved') {
      return;
    }
    
    // Only save if we have a valid context and some attendance data
    // Don't save if data was auto-filled from database or loaded from draft
    if (!selectedClassId || !activePeriodId || !selectedDate || 
        !autoFillCheckComplete || hasAutoFilledData || hasDraftData ||
        Object.keys(attendance).length === 0) {
      return;
    }

    // Debounce the save to avoid excessive writes
    const timeoutId = setTimeout(() => {
      saveDraftAttendance(attendance, notes, selectedClassId, activePeriodId, selectedDate);
    }, 1000); // Wait 1 second after last change

    return () => clearTimeout(timeoutId);
  }, [attendance, notes, selectedClassId, activePeriodId, selectedDate, autoFillCheckComplete, hasAutoFilledData, hasDraftData, reportStatus, saveDraftAttendance]);

  // Submit attendance handler
const submitAttendance = async () => {
  if (!selectedClassId) {
    window.alert('Select a class first.');
    return;
  }
  if (!activePeriodId) {
    window.alert('Select a period.');
    return;
  }
  if (students.length === 0) {
    window.alert('No students to mark.');
    return;
  }

  // Always proceed with submission, regardless of whether all students are marked
  await performSubmitAttendance();
};

// Actual submission function (called directly when all marked, or from overlay confirmation)
const performSubmitAttendance = async () => {
  // Combine regular students and historical students for submission
  const allStudentsForSubmission = [...students, ...historicalStudents];
  
  // Convert date from user's timezone to school's timezone for submission
  const schoolTimezone = currentSchool?.timezone || 'UTC';
  const schoolDate = convertDateFromUserToSchoolTimezone(selectedDate, schoolTimezone);
  
  console.log('📅 Date timezone conversion for submission:', {
    userSelectedDate: selectedDate,
    schoolTimezone: schoolTimezone,
    convertedSchoolDate: schoolDate,
    schoolName: currentSchool?.name
  });
  
  const payload = {
    classId: Number(selectedClassId),
    periodId: activePeriodId ? Number(activePeriodId) : null,
    date: schoolDate, // Use school timezone converted date
    deleteMissing: true, // Delete records for students not included in this submission
    // Only include records for students who have a status set (both regular and historical)
    records: allStudentsForSubmission
      .filter(s => attendance[s.id]) // Filter out students without status
      .map(s => ({
        studentId: Number(s.id),
        status: attendance[s.id].toUpperCase(),
        notes: notes[s.id] || null, // Include notes for the student
      })),
  };
  
  console.log('🚀 Submitting attendance payload:', {
    classId: payload.classId,
    periodId: payload.periodId,
    date: payload.date,
    originalUserDate: selectedDate,
    convertedSchoolDate: schoolDate,
    deleteMissing: payload.deleteMissing,
    recordsCount: payload.records.length,
    records: payload.records,
    allStudentsCount: allStudentsForSubmission.length,
    regularStudentsCount: students.length,
    historicalStudentsCount: historicalStudents.length,
    studentsWithAttendance: allStudentsForSubmission.filter(s => attendance[s.id]).length,
    studentsWithoutAttendance: allStudentsForSubmission.filter(s => !attendance[s.id]).length
  });
 
  const token = await getApiToken();
  try {   
    await AttendanceAPI.bulk(payload, token);
  } catch (err) {
    // If backend rejected due to missing/expired auth, do a quick popup re-login and retry once
    const isAuthError =
      /Unauthorized/i.test(err?.message || '') ||
      /Not authenticated/i.test(err?.message || '') ||
      err?.status === 401;

    if (!isAuthError) {
      throw err;
    }

    // quick re-login and retry once
    const freshToken = await getApiToken();
    await AttendanceAPI.bulk(payload, freshToken);
  }

  // success UI
  showSuccessToast('Attendance submitted successfully!');

  // Clear draft data after successful submission
  clearDraftAttendance(selectedClassId, activePeriodId, selectedDate);

  // Update report status to "saved" after successful submission
  setReportStatus('saved');
  
  // Mark that this report was just submitted (not loaded from database)
  setReportJustSubmitted(true);

  //navigate('/class-management', { state: { updatedClassId: selectedClassId } });
};

  
  // Auto-dismiss success message with proper cleanup
  useEffect(() => {
    if (toast.isVisible && toast.message) {
      // Hide the toast after 2.5 seconds
      const hideTimer = setTimeout(() => {
        setToast(prev => ({ ...prev, isVisible: false }));
      }, 2500);
      
      // Clear the message and reset z-index after fade transition completes (3 seconds total)  
      const clearTimer = setTimeout(() => {
        setToast({ message: '', isVisible: false, zIndex: 50 }); // Reset to normal z-index
      }, 3000);
      
      return () => {
        clearTimeout(hideTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [toast.isVisible, toast.message]);

  // Auto-dismiss error message after 3 seconds
  useEffect(() => {
    if (showErrorToast) {
      const timer = setTimeout(() => setShowErrorToast(false), 2500);
      const clearMsg = setTimeout(() => setErrorMessage(''), 3000);
      return () => { clearTimeout(timer); clearTimeout(clearMsg); };
    }
  }, [showErrorToast]);

  return (
    <div>
      <style>{shakeKeyframes}</style>
      <div className="max-w-[90rem] mx-auto px-0 py-2 flex flex-col gap-4 overflow-hidden">
        {/* Confetti for success (burst over submit button) */}
        {/* Removed old confetti near toast */}
        {/* Success Toast */}
        {/* Success Toast */}
        {toast.message && (
          <div 
            className="fixed bottom-8 right-8" 
            style={{ 
              zIndex: toast.zIndex,
              isolation: 'isolate', // Create new stacking context
              pointerEvents: toast.isVisible ? 'auto' : 'none'
            }}
          >
            <div className={`bg-green-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 transition-all duration-500 transform will-change-transform ${
              toast.isVisible 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-2 scale-95 pointer-events-none'
            }`}>
              <svg className="w-6 h-6 text-white flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-semibold text-lg whitespace-nowrap">{toast.message}</span>
            </div>
          </div>
        )}

        {/* Auto-fill Notification Toast */}
        {hasAutoFilledData && autoFillMessage && (
          <div className="fixed bottom-8 left-8 z-50">
            <div className="bg-blue-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 transition-opacity duration-500 opacity-100">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex flex-col">
                <span className="font-semibold">Attendance Auto-Filled</span>
                <span className="text-sm opacity-90">{autoFillMessage}</span>
              </div>
            </div>
          </div>
        )}

        {/* Draft Data Notification Toast */}
        {hasDraftData && draftMessage && (
          <div className="fixed bottom-8 left-8 z-50">
            <div className="bg-amber-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 transition-opacity duration-500 opacity-100">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <div className="flex flex-col">
                <span className="font-semibold">Draft Restored</span>
                <span className="text-sm opacity-90">{draftMessage}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error Toast with shake animation */}
        {errorMessage && (
          <div className="fixed bottom-8 right-8 z-50">
            <div
              className={`bg-red-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 transition-all duration-500 ${
                showErrorToast 
                  ? 'opacity-100 animate-[shake_0.5s_ease-in-out]' 
                  : 'opacity-0'
              }`}
              style={{ animation: showErrorToast ? 'shake 0.5s ease-in-out' : 'none' }}
            >
              <div className="flex-shrink-0 w-6 h-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-lg">Please mark all students</span>
                <span className="text-sm text-red-100 mt-0.5">{errorMessage}</span>
              </div>
            </div>
          </div>
        )}
        <AttendanceClassDateSelector
          classes={availableClasses.map(c => ({id: c.id,name: c.name,teachers: c.synced_teacher ? [c.synced_teacher] : []}))}
          selectedClass={selectedClassId ? Number(selectedClassId) : ''}
          setSelectedClass={id => handleClassChange(id ? id.toString() : '')}
          selectedDate={selectedDate}
          setSelectedDate={handleDateChange}
          teachers={(() => {
            if (!selectedClassId) return [];
            
            // Get the active period's teacher, fallback to classroom teacher
            const activePeriod = periods.find(p => p.id === activePeriodId);
            const periodTeacher = activePeriod?.synced_teacher;
            
            if (periodTeacher) {
              return [periodTeacher];
            }
            
            // Fallback to classroom teacher
            const cls = availableClasses.find(c => c.id.toString() === selectedClassId);
            return cls?.synced_teacher ? [cls.synced_teacher] : [];
          })()
         }
        />
        
        {/* Period tabs with loading indicator - only render after auto-fill check */}
        {selectedClassId && periods.length > 0 && autoFillCheckComplete && (
          <div className="flex border-b mb-2 items-center">
            {periods.map(p => (
              <button
                key={p.id}
                className={`px-4 py-2 -mb-px transition-colors ${
                  p.id === activePeriodId 
                    ? 'border-b-2 border-blue-600 text-blue-600' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => handlePeriodChange(p.id)}
                disabled={loadingStudents}
              >
                {p.name || `Period ${p.id}`}
              </button>
            ))}
            {loadingStudents && (
              <div className="ml-4 flex items-center text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Loading period...
              </div>
            )}
          </div>
        )}

        {/* Auto-fill check loading indicator - with delay to prevent brief flashes */}
        {selectedClassId && periods.length > 0 && showLoadingIndicator && (
          <div className="flex justify-center items-center py-4 text-gray-500">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
            Loading...
          </div>
        )}

        {/* No Class Selected State */}
        {!selectedClassId && (
          <div className="bg-white rounded-xl border border-dashed border-2 border-gray-200 p-12 text-center">
            <div className="flex flex-col items-center gap-6">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0h3m2 0h5M9 7h6m-6 4h6m-2 4h2M9 15h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">Select a Class to Take Attendance</h3>
                <p className="text-gray-600 mb-4 max-w-md">
                  Choose a class from the dropdown above to begin taking attendance.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Select a class to get started</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Periods Available State */}
        {selectedClassId && periods.length === 0 && !loadingStudents && (
          <div className="bg-white rounded-xl p-12 text-center border border-dashed border-2 border-gray-200">
            <div className="flex flex-col items-center gap-6">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Periods Available</h3>
                <p className="text-gray-600 mb-4 max-w-md">
                  This class doesn't have any periods assigned. Please assign periods to this class before taking attendance.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Assign periods to class</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Students in Period State */}
        {selectedClassId && periods.length > 0 && !loadingStudents && !showLoadingIndicator && autoFillCheckComplete && allStudents.length === 0 && (
          <div className="bg-white rounded-xl p-12 text-center border border-dashed border-2 border-gray-200">
            <div className="flex flex-col items-center gap-6">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Students in Period</h3>
                <p className="text-gray-600 mb-4 max-w-md">
                  This period doesn't have any students enrolled. Please enroll students in this period before taking attendance.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Enroll students in Period</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedClassId && selectedDate && !loadingStudents && autoFillCheckComplete && (
          <>
            {(() => {
              console.log('🔍 Render condition debug:', {
                selectedClassId,
                selectedDate,
                loadingStudents,
                autoFillCheckComplete,
                studentsLength: students.length,
                allStudentsLength: allStudents.length,
                activePeriodId,
                displayStudentsLength: displayStudents.length,
                hasCategories: Object.keys(originalStudentCategories).length > 0,
                studentsFromDatabaseSize: studentsFromDatabase.size
              });
              
              // Always allow rendering if we have students - we'll control individual sections
              const hasStudents = students.length > 0;
              const hasCategories = Object.keys(originalStudentCategories).length > 0;
              const hasDatabaseData = studentsFromDatabase.size > 0;
              
              // Check if categories are properly set for main student list rendering
              const categoriesMatchStudents = hasCategories && Object.keys(originalStudentCategories).length === students.length;
              const mainListReady = autoFillCheckComplete && 
                (categoriesMatchStudents || (!hasDatabaseData && reportStatus !== "checking"));
              
              console.log('🔍 Render decision:', {
                hasStudents,
                hasCategories,
                hasDatabaseData,
                categoriesMatchStudents,
                autoFillCheckComplete,
                reportStatus,
                mainListReady,
                shouldRender: hasStudents
              });
              
              return hasStudents;
            })() ? (
              (() => {
                // Calculate auto-fill settings for ReportStatusAlert
                const databaseAutoFillEnabled = isAutoFillFromDatabaseEnabled() && isAutoFillEnabled();
                const draftAutoFillEnabled = isAutoFillFromDraftsEnabled() && isAutoFillEnabled();
                const anyAutoFillEnabled = databaseAutoFillEnabled || draftAutoFillEnabled;
                
                // Recalculate mainListReady for this scope
                const hasCategories = Object.keys(originalStudentCategories).length > 0;
                const hasDatabaseData = studentsFromDatabase.size > 0;
                const categoriesMatchStudents = hasCategories && Object.keys(originalStudentCategories).length === students.length;
                const mainListReady = autoFillCheckComplete && 
                  (categoriesMatchStudents || (!hasDatabaseData && reportStatus !== "checking"));
                
                return (
                  <>
                    <AttendanceStudentList
                      students={paginatedStudents}
                      historicalStudents={studentSections.historicalStudents}
                      regularStudents={studentSections.regularStudents}
                      noteSection={studentSections.noteSection}
                      attendance={attendance}
                      setAttendance={setAttendance}
                      notes={notes}
                      setNotes={setNotes}
                      search={search}
                      setSearch={setSearch}
                      clearAll={clearAll}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      setCurrentPage={setCurrentPage}
                      totalStudents={displayStudents.length}
                      studentsPerPage={studentsPerPage}
                      markAllPresent={markAllPresent}
                      markAllAbsent={markAllAbsent}
                      markAllLate={markAllLate}
                      markAllExcused={markAllExcused}
                      reportStatus={reportStatus}
                      databaseAutoFillInProgress={!autoFillCheckComplete}
                      autoFillCheckComplete={autoFillCheckComplete}
                      databaseAutoFillEnabled={databaseAutoFillEnabled}
                      anyAutoFillEnabled={anyAutoFillEnabled}
                      onAddStudent={handleAddStudent}
                      onDeleteReport={handleDeleteReport}
                      enableUnselectedStudents={enableUnselectedStudents}
                      setEnableUnselectedStudents={setEnableUnselectedStudents}
                      mainListReady={mainListReady}
                    />
                    <AttendanceActions
                      ref={submitBtnRef}
                      submitAttendance={submitAttendance}
                      showConfetti={toast.isVisible}
                    />
                  </>
                );
              })()
            ) : null}
          </>
        )}

        {/* Loading state for students */}
        {selectedClassId && selectedDate && loadingStudents && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-gray-600">Loading students...</span>
          </div>
        )}

        {/* Add Student Modal */}
        <AddStudentModal
          isOpen={showAddStudentModal}
          onClose={handleCloseAddStudentModal}
          classId={selectedClassId}
          periods={periods}
          activePeriodId={activePeriodId}
          onNewStudentCreated={handleNewStudentCreated}
          onExistingStudentsEnrolled={handleExistingStudentsEnrolled}
        />

        {/* Delete Report Confirmation Modal */}
        {showDeleteConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.966-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Attendance Report</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete this attendance report? All attendance data for this class, period, and date will be permanently removed.
                </p>
                {selectedDate && activePeriodId && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="text-gray-600">
                      <strong>Date:</strong> {selectedDate}
                    </div>
                    <div className="text-gray-600">
                      <strong>Period:</strong> {periods.find(p => p.id === activePeriodId)?.name || 'Unknown Period'}
                    </div>
                    <div className="text-gray-600">
                      <strong>Class:</strong> {availableClasses.find(c => c.id.toString() === selectedClassId)?.name || 'Unknown Class'}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirmModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteReport}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Report
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Incomplete Attendance Warning Overlay */}
    </div>
  );
} 
